// Sprint-logikk uten DOM.
//
// DET SOM MÅ HOLDES FRA HVERANDRE:
// `tidsandel` er hvor mye av TIDEN som er gått. `fremdrift.andel` er hvor mye av
// ARBEIDET som er ferdig. Kontrakten sier det rett ut: «Å slå dem sammen i én
// søyle er den klassiske løgnen i sprint-visninger: en sprint kan være 90 %
// gjennom i tid og 20 % gjennom i arbeid, og det er selve funnet.» Denne fila
// regner ut avviket som et EGET tall, slik at UI-et kan si det høyt i stedet for
// å gjemme det i en felles søyle.
//
// FORMEN PÅ EN SPRINT-RAD (rettet i kontrakten 2026-07-27):
//
//   { sprint: Sprint, status: SprintStatus, fremdrift: Fremdrift }
//
// De tre er SIDESTILTE, ikke spredd inn i hverandre. Den forrige formen spredte
// sprint-feltene på toppnivå og la statusobjektet på `status` — men `Sprint` har
// allerede et `status`-felt (livssyklusen planlagt/aktiv/ferdig/avbrutt), så
// enumen ble overskrevet og havnet på `status.sprint.status`. Ingen klient skal
// måtte kjenne den stien.
//
// Leseren under tar den GJELDENDE formen først, men godtar fortsatt de to gamle.
// Grunnen er ikke nostalgi: en utrullet tjeneste og en utrullet klient bytter
// ikke form i samme sekund, og en klient som blir hvit i mellomtiden er verre
// enn tjue linjer toleranse. Innover i klienten heter enumen `tilstand`, så
// navnekollisjonen ikke får leve videre.
import type { Fremdrift, Sprint } from '@skade/dugnad/prosjekt';

export type Sprinttilstand = Sprint['status'];

const TILSTANDER: readonly Sprinttilstand[] = ['planlagt', 'aktiv', 'ferdig', 'avbrutt'];

export const TILSTAND_NAVN: Record<Sprinttilstand, string> = {
  planlagt: 'Planlagt',
  aktiv: 'Aktiv',
  ferdig: 'Ferdig',
  avbrutt: 'Avbrutt',
};

export interface SprintVisning {
  id: string;
  navn: string;
  mal: string;
  startTsMs: number;
  sluttTsMs: number;
  kort: string[];
  tilstand: Sprinttilstand;
  dagerTotalt: number;
  dagerIgjen: number;
  /** Andel av tiden som er gått (0–1). Sier ingenting om arbeidet. */
  tidsandel: number;
  antallKort: number;
  /** Andel av arbeidet som er ferdig (0–1). Sier ingenting om tiden. */
  fremdrift: Fremdrift;
}

function tall(v: unknown, standard = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : standard;
}

function tilstandAv(v: unknown): Sprinttilstand {
  return typeof v === 'string' && (TILSTANDER as readonly string[]).includes(v)
    ? (v as Sprinttilstand)
    : 'planlagt';
}

function fremdriftAv(v: unknown): Fremdrift {
  const f = (v ?? {}) as Partial<Fremdrift>;
  return {
    totalt: tall(f.totalt), ferdig: tall(f.ferdig),
    paagaar: tall(f.paagaar), andel: tall(f.andel),
  };
}

function objekt(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/**
 * Én rad fra `GET /sprinter` → det UI-et viser. Returnerer null for rader uten
 * id eller navn: en sprint uten navn er ikke en sprint, og å vise den som en tom
 * rad ville tatt plass i lista uten å bety noe.
 */
export function normaliserSprint(rå: unknown): SprintVisning | null {
  const r = objekt(rå);
  if (!r) return null;

  // Gjeldende form: sprint-feltene ligger under `sprint`, tidstallene under
  // `status`. `statusObjekt.sprint` er den GAMLE plasseringen og leses bare som
  // reserve — se filhodet.
  const statusObjekt = objekt(r.status);
  const sprintDel = objekt(r.sprint) ?? objekt(statusObjekt?.sprint) ?? r;
  const tidDel = statusObjekt ?? r;

  const id = typeof sprintDel.id === 'string' ? sprintDel.id : '';
  const navn = typeof sprintDel.navn === 'string' ? sprintDel.navn : '';
  if (!id || !navn) return null;

  const kortListe = Array.isArray(sprintDel.kort)
    ? (sprintDel.kort as unknown[]).filter((x): x is string => typeof x === 'string')
    : [];

  return {
    id,
    navn,
    mal: typeof sprintDel.mal === 'string' ? sprintDel.mal : '',
    startTsMs: tall(sprintDel.startTsMs),
    sluttTsMs: tall(sprintDel.sluttTsMs),
    kort: kortListe,
    // Livssyklusen hører til SPRINTEN, aldri til statusobjektet.
    tilstand: tilstandAv(sprintDel.status),
    dagerTotalt: tall(tidDel.dagerTotalt),
    dagerIgjen: tall(tidDel.dagerIgjen),
    tidsandel: tall(tidDel.tidsandel),
    antallKort: tall(tidDel.antallKort, kortListe.length),
    fremdrift: fremdriftAv(r.fremdrift),
  };
}

// ── Tid mot arbeid ──────────────────────────────────────────────────────────

export type Takt = 'i takt' | 'bak' | 'foran';

export interface TidMotArbeid {
  tidsandel: number;
  arbeidsandel: number;
  /** tidsandel − arbeidsandel. Positivt = mer tid brukt enn arbeid gjort. */
  differanse: number;
  takt: Takt;
  /** Én setning som sier hva de to tallene betyr TILSAMMEN. */
  forklaring: string;
}

/** Under denne forskjellen er avviket støy, ikke et funn. */
export const TAKT_TERSKEL = 0.1;

function prosent(v: number): number {
  return Math.round(Math.max(0, Math.min(1, v)) * 100);
}

/**
 * Sammenlign tid brukt med arbeid gjort — som to tall, ikke ett.
 *
 * Teksten sier begge tallene hver gang. Det er med vilje: en bruker som bare
 * ser «30 % bak» vet ikke om det er tiden eller arbeidet som er problemet.
 */
export function tidMotArbeid(tidsandel: number, arbeidsandel: number): TidMotArbeid {
  const t = Math.max(0, Math.min(1, tall(tidsandel)));
  const a = Math.max(0, Math.min(1, tall(arbeidsandel)));
  const differanse = t - a;
  const takt: Takt = Math.abs(differanse) < TAKT_TERSKEL ? 'i takt' : differanse > 0 ? 'bak' : 'foran';
  const pt = prosent(t);
  const pa = prosent(a);
  const forklaring = takt === 'i takt'
    ? `${pt} % av tiden er gått og ${pa} % av arbeidet er ferdig — sprinten følger tiden.`
    : takt === 'bak'
      ? `${pt} % av tiden er gått, men bare ${pa} % av arbeidet er ferdig. `
        + `Arbeidet ligger ${prosent(differanse)} prosentpoeng etter tiden.`
      : `${pa} % av arbeidet er ferdig etter ${pt} % av tiden. `
        + `Arbeidet ligger ${prosent(-differanse)} prosentpoeng foran tiden.`;
  return { tidsandel: t, arbeidsandel: a, differanse, takt, forklaring };
}

/** Sprinter sortert slik en stab vil se dem: aktive først, så etter starttid. */
export function sorterSprinter(liste: readonly SprintVisning[]): SprintVisning[] {
  const rang: Record<Sprinttilstand, number> = { aktiv: 0, planlagt: 1, ferdig: 2, avbrutt: 3 };
  return [...liste].sort((a, b) =>
    rang[a.tilstand] - rang[b.tilstand]
    || a.startTsMs - b.startTsMs
    || a.navn.localeCompare(b.navn, 'nb'));
}

export interface SprintUtkast {
  navn: string;
  mal: string;
  startTsMs: number;
  sluttTsMs: number;
}

/**
 * Validér et sprint-skjema før det sendes. Kontrakten sier at `sluttTsMs` må
 * være etter `startTsMs` (ellers 400), og `sprintStatus()` deler på varigheten —
 * en sprint på 0 ms er en divisjon vi ikke skal komme til.
 */
export function validerSprint(u: Partial<SprintUtkast>): { felt: string; grunn: string } | null {
  if (!u.navn || !u.navn.trim()) return { felt: 'navn', grunn: 'sprinten må ha et navn' };
  if (u.navn.trim().length > 120) return { felt: 'navn', grunn: 'navnet kan være høyst 120 tegn' };
  if (!Number.isFinite(u.startTsMs)) return { felt: 'start', grunn: 'oppgi en startdato' };
  if (!Number.isFinite(u.sluttTsMs)) return { felt: 'slutt', grunn: 'oppgi en sluttdato' };
  if ((u.sluttTsMs as number) <= (u.startTsMs as number)) {
    return { felt: 'slutt', grunn: 'sluttdatoen må være etter startdatoen' };
  }
  if ((u.mal ?? '').length > 1000) return { felt: 'mal', grunn: 'målet kan være høyst 1000 tegn' };
  return null;
}
